import type { Nullable } from '@library/types'
import { identity } from '@library/utils'
import type { KeysOfUnion } from 'common/types'
import type { ValidationErrors } from 'fluentvalidation-ts'

import { useCheckValidity } from './useCheckValidity'
import { useSyncState } from './useSyncState'

/**
 * Хук управления состоянием многих полей ввода разных типов
 * @param option.initialValue -- начальное значение формы, синхронизируется в соответствии с isReadyToSync и syncOnce
 * @param option.converters -- набор конвертеров формы из опциональных строк в значения
 * @param option.isValidCheck -- валидатор формы fluentvalidation-ts
 * @param option.syncOnce -- требуется ли синхронизировать один раз, а не поддерживать синхронизацию
 * @param option.isReadyToSync -- коллбек, решающий, когда новое значение формы готово для синхронизации
 * @param option.onChange -- обработчик изменения формы целиком
 * @param option.onApply -- событие при применении формы
 *
 * // TODO: имеется копия в utils, оттуда нужно убрать
 */
export const useHandleInput = <
  T extends Record<string, unknown>,
  Apply extends (...params: unknown[]) => unknown,
>({
  initialValue,
  converters,
  isValidCheck,
  syncOnce = true,
  isReadyToSync,
  onChange,
  onApply,
}: UseHandleInputOptions<T, Apply>) => {
  type Keys = KeysOfUnion<T>

  const [state, setState] = useSyncState(initialValue, syncOnce, isReadyToSync)
  const [validation, isValid, setChecked] = useCheckValidity(isValidCheck, state)

  const handle = (key: Keys) => (newValue: Nullable<string>) => {
    const converter = converters?.[key] ?? identity
    const value = converter(newValue)
    const newState = { ...state, [key]: value }
    setState(newState)
    const isValid = setChecked(newState)
    if (isValid) {
      onChange(newState)
    }
  }

  const getDefaultValue = (key: Keys) => () => {
    const converter = converters?.[key] ?? identity
    const value = converter(null)
    return value?.toString() ?? null
  }

  const setCheckedState = (resetState: T) => {
    setState(resetState)
    setChecked(resetState)
  }

  const handleApply = ((...params) => {
    setCheckedState(initialValue)
    return onApply?.(...params)
  }) as Apply

  return {
    state,
    handle,
    handleApply,
    isValid,
    setStateRaw: setCheckedState,
    validation,
    getDefaultValue,
  } as const
}

type UseHandleInputOptions<T, Apply extends (...params: unknown[]) => unknown> = {
  onApply?: Apply
  initialValue: T
  converters?: { [Key in keyof T]?: (value: Nullable<string>) => T[Key] }
  isValidCheck: (value: T) => ValidationErrors<T>
  syncOnce?: boolean
  isReadyToSync?: (value: T) => boolean
  onChange: (value: T) => void
}
